import React, { useState } from 'react';
import { XIcon, SearchIcon, ChevronDownIcon } from 'lucide-react';
interface Service {
  id: string;
  title: string;
  description: string;
  category: string;
  estimatedTime: string;
  fee: string;
}
// Organized services by section
const servicesBySection = {
  'Land Title Services': [{
    id: 'lt1',
    title: 'New Land Title Registration',
    description: 'Apply for first-time registration of land title',
    category: 'Land Title Services',
    estimatedTime: '15-20 business days',
    fee: '$250.00'
  }, {
    id: 'lt2',
    title: 'Land Title Transfer',
    description: 'Transfer land ownership through sale, inheritance, or gift',
    category: 'Land Title Services',
    estimatedTime: '10-15 business days',
    fee: '$200.00'
  }, {
    id: 'lt3',
    title: 'Title Rectification',
    description: 'Apply for correction of land title information',
    category: 'Land Title Services',
    estimatedTime: '5-10 business days',
    fee: '$150.00'
  }, {
    id: 'lt4',
    title: 'Land Sub-division',
    description: 'Application for subdividing land title',
    category: 'Land Title Services',
    estimatedTime: '20-25 business days',
    fee: '$300.00'
  }, {
    id: 'lt5',
    title: 'Land Amalgamation',
    description: 'Combine multiple land titles into one',
    category: 'Land Title Services',
    estimatedTime: '20-25 business days',
    fee: '$300.00'
  }, {
    id: 'lt6',
    title: 'Lease Registration',
    description: 'Register a new land lease agreement',
    category: 'Land Title Services',
    estimatedTime: '7-10 business days',
    fee: '$175.00'
  }],
  'Property Development': [{
    id: 'pd1',
    title: 'Building Permit - New Construction',
    description: 'Apply for permit to construct new buildings',
    category: 'Property Development',
    estimatedTime: '15-20 business days',
    fee: '$500.00'
  }, {
    id: 'pd2',
    title: 'Renovation Permit',
    description: 'Get approval for property renovations or extensions',
    category: 'Property Development',
    estimatedTime: '10-15 business days',
    fee: '$250.00'
  }, {
    id: 'pd3',
    title: 'Demolition Permit',
    description: 'Apply for permission to demolish existing structures',
    category: 'Property Development',
    estimatedTime: '7-10 business days',
    fee: '$200.00'
  }],
  'Information Requests': [{
    id: 'ir1',
    title: 'Official Land Search',
    description: 'Request official land title verification',
    category: 'Information Requests',
    estimatedTime: '2-3 business days',
    fee: '$50.00'
  }, {
    id: 'ir2',
    title: 'Cadastral Map Request',
    description: 'Get detailed property boundary information',
    category: 'Information Requests',
    estimatedTime: '3-5 business days',
    fee: '$75.00'
  }, {
    id: 'ir3',
    title: 'Survey Data Request',
    description: 'Access property survey information',
    category: 'Information Requests',
    estimatedTime: '3-5 business days',
    fee: '$100.00'
  }],
  'Permits & Certificates': [{
    id: 'pc1',
    title: 'Occupancy Certificate',
    description: 'Apply for building occupancy certification',
    category: 'Permits & Certificates',
    estimatedTime: '5-7 business days',
    fee: '$150.00'
  }, {
    id: 'pc2',
    title: 'Hoarding Permit',
    description: 'Get permission for temporary construction barriers',
    category: 'Permits & Certificates',
    estimatedTime: '3-5 business days',
    fee: '$100.00'
  }, {
    id: 'pc3',
    title: 'Property Address Assignment',
    description: 'Request official property address confirmation',
    category: 'Permits & Certificates',
    estimatedTime: '5-7 business days',
    fee: '$50.00'
  }]
};
interface ServiceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onServiceSelect: (service: Service) => void;
}
export const ServiceSelectionModal = ({
  isOpen,
  onClose,
  onServiceSelect
}: ServiceSelectionModalProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [expandedSections, setExpandedSections] = useState<string[]>(Object.keys(servicesBySection));
  if (!isOpen) return null;
  const toggleSection = (section: string) => {
    setExpandedSections(current => current.includes(section) ? current.filter(s => s !== section) : [...current, section]);
  };
  // Filter services based on search query and selected category
  const filteredSections = Object.entries(servicesBySection).reduce((acc, [section, services]) => {
    const filteredServices = services.filter(service => (selectedCategory === 'all' || service.category === selectedCategory) && (service.title.toLowerCase().includes(searchQuery.toLowerCase()) || service.description.toLowerCase().includes(searchQuery.toLowerCase())));
    if (filteredServices.length > 0) {
      acc[section] = filteredServices;
    }
    return acc;
  }, {} as typeof servicesBySection);
  return <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-376">
      <div className="flex items-center justify-center min-h-screen px-4" data-id="element-377">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" data-id="element-378" />
        <div className="relative bg-white rounded-lg max-w-4xl w-full mx-auto shadow-xl" data-id="element-379">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200" data-id="element-380">
            <h2 className="text-xl font-semibold text-gray-900" data-id="element-381">
              Select a Service
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500" data-id="element-382">
              <XIcon className="h-5 w-5" data-id="element-383" />
            </button>
          </div>
          {/* Search and Filter */}
          <div className="p-6 border-b border-gray-200" data-id="element-384">
            <div className="flex gap-4" data-id="element-385">
              <div className="flex-1 relative" data-id="element-386">
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" data-id="element-387" />
                <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm" placeholder="Search services..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-388" />
              </div>
              <div className="w-64" data-id="element-389">
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} data-id="element-390">
                  <option value="all" data-id="element-391">All Categories</option>
                  <option value="Land Title Services" data-id="element-392">
                    Land Title Services
                  </option>
                  <option value="Property Development" data-id="element-393">
                    Property Development
                  </option>
                  <option value="Information Requests" data-id="element-394">
                    Information Requests
                  </option>
                  <option value="Permits & Certificates" data-id="element-395">
                    Permits & Certificates
                  </option>
                </select>
              </div>
            </div>
          </div>
          {/* Services List */}
          <div className="max-h-[60vh] overflow-y-auto p-6" data-id="element-396">
            {Object.entries(filteredSections).map(([section, services]) => <div key={section} className="mb-6 last:mb-0" data-id="element-397">
                <button className="flex items-center justify-between w-full text-left mb-4" onClick={() => toggleSection(section)} data-id="element-398">
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-399">
                    {section}
                  </h3>
                  <ChevronDownIcon className={`h-5 w-5 text-gray-500 transform transition-transform ${expandedSections.includes(section) ? 'rotate-180' : ''}`} data-id="element-400" />
                </button>
                {expandedSections.includes(section) && <div className="space-y-4 ml-4" data-id="element-401">
                    {services.map(service => <button key={service.id} className="w-full text-left p-5 border border-gray-200 rounded-lg hover:border-blue-500 hover:shadow-md transition-all duration-200 group relative overflow-hidden bg-white" onClick={() => onServiceSelect(service)} data-id="element-402">
                        <div className="absolute inset-0 bg-blue-50 opacity-0 group-hover:opacity-10 transition-opacity duration-200" onClick={() => {
                  onServiceSelect(service);
                  // Navigate to the identify citizen page with the selected service ID
                  window.location.href = `/assisted-creation/identify-citizen/${service.id}`;
                }} data-id="element-403"></div>
                        <h4 className="text-lg font-medium text-gray-900 mb-2 group-hover:text-blue-700 transition-colors duration-200" data-id="element-404">
                          {service.title}
                        </h4>
                        <p className="text-sm text-gray-600 mb-5 line-clamp-2" data-id="element-405">
                          {service.description}
                        </p>
                        <div className="flex items-center justify-between text-sm text-gray-500" data-id="element-406">
                          <div className="flex items-center space-x-4" data-id="element-407">
                            <span className="flex items-center bg-gray-50 px-2 py-1 rounded-md" data-id="element-408">
                              <span className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-409">
                                ⏱
                              </span>
                              <span className="font-medium" data-id="element-410">
                                {service.estimatedTime}
                              </span>
                            </span>
                            <span className="flex items-center bg-gray-50 px-2 py-1 rounded-md" data-id="element-411">
                              <span className="w-4 h-4 text-gray-400 mr-1.5" data-id="element-412">
                                💰
                              </span>
                              <span className="font-medium" data-id="element-413">{service.fee}</span>
                            </span>
                          </div>
                          <div className="inline-flex items-center justify-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-all duration-200 shadow-sm group-hover:shadow-md transform group-hover:translate-y-[-1px]" data-id="element-414">
                            <span className="mr-1" data-id="element-415">Start Request</span>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" viewBox="0 0 20 20" fill="currentColor" data-id="element-416">
                              <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" data-id="element-417" />
                            </svg>
                          </div>
                        </div>
                      </button>)}
                  </div>}
              </div>)}
          </div>
          {/* Footer */}
          <div className="flex justify-end p-6 border-t border-gray-200" data-id="element-418">
            <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-419">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>;
};